// Aviso global de la Sala (Sala CRM↔HCE, rebanada 5; PLAN 4.11): mantiene el contador de pacientes en espera del menú y avisa «X llegó» con un aviso
// discreto — SIN sonido y sin quitar el foco de lo que el médico escribe (un toast de sonner no roba el foco).
//
// Se monta UNA vez en `App` (visible desde cualquier pantalla). Usa el canal en vivo (`useEventosSala`, con su respaldo de 30 s y la desduplicación
// entre pestañas) y consulta el resumen ligero `GET /api/hce/sala/resumen`. Cada pestaña mantiene su contador al día, pero el AVISO solo lo muestra la
// pestaña líder: con varias pestañas abiertas el médico ve un solo «llegó», no uno por pestaña. La líder de los avisos se elige con su propio bloqueo
// (ver lib/liderAvisos.ts: el `esLider` del canal es por instancia del hook y no sirve para esto).
import { useMedplumProfile } from '@medplum/react-hooks';
import type { JSX } from 'react';
import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { notify } from '@/components/ui/sonner';
import { ErrorSala, esLlegadaVigente, leerAvisados, llegadasNuevas, marcarAvisados, obtenerResumenSala, publicarContadorSala } from '@/lib/salaHce';
import { useLiderDeAvisos } from '@/lib/liderAvisos';
import { useEventosSala } from '@/lib/useEventosSala';

export function AvisosSala(): JSX.Element | null {
  // Sin sesión de Medplum (landing, login) no hay nada que consultar.
  const profile = useMedplumProfile();
  return profile ? <AvisosSalaActivos /> : null;
}

function AvisosSalaActivos(): JSX.Element | null {
  const { t } = useTranslation();
  /** Ids ya vistos; `null` = primera carga (no se avisa de lo que ya esperaba al abrir). */
  const vistos = useRef<Set<number> | null>(null);
  /** Un rol sin Sala (director técnico de farmacia) recibe 403: no se vuelve a consultar. */
  const sinSala = useRef(false);
  const esLiderRef = useRef(false);

  const actualizar = useCallback(async () => {
    if (sinSala.current) return;
    try {
      const resumen = await obtenerResumenSala();
      publicarContadorSala({ disponible: true, esperando: resumen.esperando });
      if (vistos.current === null) {
        // Primera carga de esta pestaña: lo que ya esperaba no es una llegada nueva.
        vistos.current = new Set(resumen.enEspera.map((e) => e.id));
        return;
      }
      // Solo la líder (la pestaña VISIBLE que tiene el bloqueo) avisa. Una pestaña que no lidera NO da por vistas las llegadas: quedan pendientes y
      // se avisan cuando recupere el liderazgo; `avisados` (compartido entre pestañas) evita repetir las que otra ya avisó.
      if (!esLiderRef.current) return;
      const avisados = leerAvisados();
      const porAvisar = llegadasNuevas(vistos.current, resumen).filter((n) => !avisados.has(n.id));
      for (const n of porAvisar) {
        if (esLlegadaVigente(n.llegadaAt, resumen.ahora)) notify({ color: 'yellow', message: t('sala.aviso.llego', { nombre: n.nombre }), autoClose: 7000 });
      }
      marcarAvisados(porAvisar.map((n) => n.id));
      vistos.current = new Set([...vistos.current, ...resumen.enEspera.map((e) => e.id)]);
    } catch (err) {
      if (err instanceof ErrorSala && (err.status === 403 || err.status === 401)) {
        // 403: este rol no tiene Sala (nunca la tendrá en esta sesión). 401: sesión vencida; el resto de la app ya lo maneja.
        if (err.status === 403) sinSala.current = true;
        publicarContadorSala({ disponible: false, esperando: 0 });
      }
      // Cualquier otro fallo (red, 5xx): se conserva el último contador; el respaldo de 30 s reintenta.
    }
  }, [t]);

  useEventosSala(() => void actualizar(), { tipos: ['sala'] });
  const esLider = useLiderDeAvisos(true);
  esLiderRef.current = esLider;

  useEffect(() => {
    void actualizar();
  }, [actualizar]);

  // Al pasar a líder (la pestaña volvió a verse, o se cerró la anterior) se revisa lo que llegó mientras no lideraba.
  useEffect(() => {
    if (esLider) void actualizar();
  }, [esLider, actualizar]);

  return null;
}
